import { useMemo, useState, useEffect } from "react";
import { Dayjs } from "dayjs";
import {
  QueryOperator,
  EnumOperator,
  DateTimeOperator,
  CustomMetadataLinkOperators,
  CUSTOM_METADATA_ENUM_OPERATORS,
  CUSTOM_METADATA_DATETIME_OPERATORS,
  CUSTOM_METADATA_LINK_OPERATORS,
  CUSTOM_METADATA_ARRAY_STRING_OPERATORS,
  FilterOption,
  CustomMetadata,
  CustomMetadataFieldType,
  APIQueryOperators,
  VqlModeOps,
  CustomMetadataOperators,
  ArrayStringOperator,
} from "types";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { useCustomMetadataModal } from "hooks/useCustomMetadataModal";
import {
  parseDateFromAPI,
  parseDateRangeFromAPI,
} from "helpers/utility/customMetadataHelpers";
import useVqlParams from "hooks/useVqlParams";
import {
  getExistingCustomMetadataQuery,
  getCustomMetadataQueryValue,
} from "helpers/utility/vqlHelpers";

type FieldType = Exclude<
  CustomMetadataFieldType,
  "string" | "float" | "geolocation"
>;

interface UseModalOperatorsAndValuesProps {
  type: FieldType;
  field: CustomMetadata;
  editQueryId?: string;
  options: string[];
}

// Base interface with truly common properties
interface BaseReturn<T = any> {
  operator: T;
  operatorOptions: T[];
  setOperator: (op: T) => void;
  previousValue?: string[];
  previousOperator?: EnumOperator | null;
}

// Specific return types extending the base
interface EnumReturn extends BaseReturn<EnumOperator> {
  selectedIds: string[];
  values: FilterOption[];
  setValues: (v: FilterOption[]) => void;
  searchText: string;
  setSearchText: (s: string) => void;
  existingQuery?: any;
}

interface LinkReturn extends BaseReturn<CustomMetadataLinkOperators> {
  searchText: string;
  selectedIds: string[];
  values: FilterOption[];
  mode: "text" | "multiple";
  setSearchText: (s: string) => void;
  setValues: (v: FilterOption[]) => void;
  existingQuery?: any;
}

interface DatetimeReturn extends BaseReturn<DateTimeOperator | null> {
  value: { single: Dayjs | null; range: [Dayjs, Dayjs] | null };
  setValue: (v: { single: Dayjs | null; range: [Dayjs, Dayjs] | null }) => void;
  mode: "Single" | "Range";
  existingQuery?: any;
}

interface StringArrayReturn extends BaseReturn<ArrayStringOperator> {
  selectedIds: string[];
  values: FilterOption[];
  setValues: (v: FilterOption[]) => void;
  searchText: string;
  setSearchText: (s: string) => void;
  existingQuery?: any;
}

// Centralized configuration
const FIELD_CONFIG = {
  enum: {
    operators: CUSTOM_METADATA_ENUM_OPERATORS,
    defaultOperator: QueryOperator.IS_ONE_OF,
    isMultiValue: true,
    needsValues: true,
    needsSearchText: false,
    needsDateTimeValue: false,
  },
  link: {
    operators: CUSTOM_METADATA_LINK_OPERATORS,
    defaultOperator: QueryOperator.CONTAINS,
    isMultiValue: false,
    needsValues: true,
    needsSearchText: true,
    needsDateTimeValue: false,
  },
  datetime: {
    operators: CUSTOM_METADATA_DATETIME_OPERATORS,
    defaultOperator: null,
    isMultiValue: false,
    needsValues: false,
    needsSearchText: false,
    needsDateTimeValue: true,
  },
  string_array: {
    operators: CUSTOM_METADATA_ARRAY_STRING_OPERATORS,
    defaultOperator: QueryOperator.IS_ONE_OF,
    isMultiValue: true,
    needsValues: true,
    needsSearchText: false,
    needsDateTimeValue: false,
  },
} as const;

const formatOptions = (
  options: string[],
  selected?: string[],
): FilterOption[] =>
  options.map((text, id) => ({
    id: String(id),
    text,
    selected: selected?.includes(text) || false,
  }));

// Helper to determine datetime mode based on operator
const getDateTimeMode = (
  operator: DateTimeOperator | null,
): "Single" | "Range" => {
  if (operator === QueryOperator.BETWEEN) return "Range";
  return "Single";
};

// Function overloads
export function useModalOperatorsAndValues(
  props: UseModalOperatorsAndValuesProps & { type: "enum" },
): EnumReturn;
export function useModalOperatorsAndValues(
  props: UseModalOperatorsAndValuesProps & { type: "link" },
): LinkReturn;
export function useModalOperatorsAndValues(
  props: UseModalOperatorsAndValuesProps & { type: "datetime" },
): DatetimeReturn;
export function useModalOperatorsAndValues(
  props: UseModalOperatorsAndValuesProps & { type: "string_array" },
): StringArrayReturn;
export function useModalOperatorsAndValues({
  type,
  field,
  editQueryId,
  options,
}: UseModalOperatorsAndValuesProps) {
  const { oldQuery } = useCustomMetadataModal({ field, editQueryId });
  const oldOp = oldQuery?.custom_metadata?.op;

  const config = FIELD_CONFIG[type];
  const defaultOp = oldOp ? convertAPIOperator(oldOp) : config.defaultOperator;

  // Common state
  const [operator, setOperator] = useState(defaultOp);

  const { vqlQueries } = useVqlParams();

  const existingQuery = useMemo(() => {
    return getExistingCustomMetadataQuery(
      vqlQueries,
      field.field_id,
      operator as CustomMetadataOperators,
    );
  }, [operator, field.field_id, vqlQueries]);

  const oldValue = useMemo(() => {
    return getCustomMetadataQueryValue(
      editQueryId,
      oldQuery,
      operator as QueryOperator,
      defaultOp as QueryOperator,
      existingQuery,
    );
  }, [editQueryId, oldQuery, operator, defaultOp, existingQuery]);

  // Conditional states based on config
  const isMulti =
    config.isMultiValue ||
    (type === "link" && operator === QueryOperator.IS_ONE_OF);

  const [values, setValues] = useState<FilterOption[]>(() =>
    config.needsValues
      ? isMulti
        ? formatOptions(options, oldValue as string[])
        : formatOptions(options)
      : [],
  );

  const [searchText, setSearchText] = useState(() =>
    config.needsSearchText && !isMulti ? (oldValue as string) || "" : "",
  );

  useEffect(() => {
    // Sync VALUES
    if (config.needsValues) {
      setValues(
        isMulti
          ? formatOptions(options, oldValue as string[])
          : formatOptions(options),
      );
    }

    // Sync SEARCH TEXT
    if (config.needsSearchText) {
      setSearchText(!isMulti && typeof oldValue === "string" ? oldValue : "");
    }

    if (config.needsDateTimeValue) {
      setDateTimeValue({
        single:
          !Array.isArray(oldValue) && oldValue
            ? parseDateFromAPI(oldValue as string)
            : null,
        range:
          Array.isArray(oldValue) && oldValue.length === 2
            ? parseDateRangeFromAPI(oldValue as [string, string])
            : null,
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operator]);

  const [dateTimeValue, setDateTimeValue] = useState(() => ({
    single:
      config.needsDateTimeValue && !Array.isArray(oldValue)
        ? parseDateFromAPI(oldValue as string)
        : null,
    range:
      config.needsDateTimeValue && Array.isArray(oldValue)
        ? parseDateRangeFromAPI(oldValue as [string, string])
        : null,
  }));

  useEffect(() => {
    if (!config.needsValues) return;
    setValues(
      isMulti
        ? formatOptions(options, oldValue as string[])
        : formatOptions(options),
    );
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  // Common computed values
  const selectedIds = values.filter((v) => v.selected).map((v) => v.id);
  const operatorOptions = [...config.operators];

  // Common return properties
  const commonReturn = { operatorOptions, setOperator };

  // Type-specific return builders with shared properties
  const returnBuilders: Record<FieldType, () => any> = {
    enum: () => ({
      ...commonReturn,
      operator: operator as EnumOperator,
      values,
      setValues,
      selectedIds,
      searchText,
      setSearchText,
      previousValue: oldValue,
      previousOperator: defaultOp,
      existingQuery,
    }),

    link: () => ({
      ...commonReturn,
      operator: operator as CustomMetadataLinkOperators,
      values,
      setValues,
      selectedIds,
      searchText,
      setSearchText,
      mode: operator === QueryOperator.IS_ONE_OF ? "multiple" : "text",
      previousValue: oldValue,
      previousOperator: convertAPIOperator(
        oldOp as APIQueryOperators | VqlModeOps,
      ),
      existingQuery,
    }),

    datetime: () => ({
      ...commonReturn,
      operator: operator as DateTimeOperator | null,
      value: dateTimeValue,
      setValue: setDateTimeValue,
      mode: getDateTimeMode(operator as DateTimeOperator | null),
      existingQuery,
      previousValue: oldValue,
    }),

    string_array: () => ({
      ...commonReturn,
      operator: operator as ArrayStringOperator,
      values,
      setValues,
      selectedIds,
      searchText,
      setSearchText,
      previousValue: oldValue,
      previousOperator: defaultOp,
      existingQuery,
    }),
  };

  return returnBuilders[type]();
}
